// src/components/payroll/reports/PayrollReports.js
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { AppContext } from '../../../context/AppContext';
import { db } from '../../../firebase/config';
import { 
  collection, query, where, orderBy, getDocs 
} from 'firebase/firestore';
import { useCurrency } from '../../../utils/currencyUtils';
import {
  ChevronLeft, BarChart2, PieChart, Download, Calendar,
  FileText, DollarSign, User, Briefcase, ChevronDown
} from 'lucide-react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from 'chart.js';
import { Bar, Pie } from 'react-chartjs-2';

// Register ChartJS components
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

const PayrollReports = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { activeBusiness } = useContext(AppContext);
  const { formatAmount } = useCurrency();
  
  const [loading, setLoading] = useState(true);
  const [payrollData, setPayrollData] = useState({
    summary: {
      totalPaid: 0,
      employeeCount: 0,
      avgSalary: 0,
      avgTaxRate: 0,
      payRunCount: 0
    },
    monthly: [],
    departmentDistribution: [],
    taxDistribution: []
  });
  const [timeFrame, setTimeFrame] = useState('year');
  const [expandedSection, setExpandedSection] = useState('all');
  
  // Fetch payroll report data
  useEffect(() => {
    const fetchPayrollData = async () => {
      if (!currentUser || !activeBusiness) return;
      
      try {
        setLoading(true);
        
        // Get date range based on timeframe
        const now = new Date();
        let startDate;
        
        if (timeFrame === 'year') {
          startDate = new Date(now.getFullYear(), 0, 1); // Jan 1 of current year
        } else if (timeFrame === 'quarter') {
          const quarter = Math.floor(now.getMonth() / 3);
          startDate = new Date(now.getFullYear(), quarter * 3, 1);
        } else if (timeFrame === 'month') {
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        } else {
          // Default to last 12 months
          startDate = new Date(now.getFullYear() - 1, now.getMonth(), 1);
        }
        
        // Fetch pay runs in the date range
        const payRunsQuery = query(
          collection(db, 'payRuns'),
          where('businessId', '==', activeBusiness.id),
          where('status', '==', 'completed'),
          where('paymentDate', '>=', startDate),
          orderBy('paymentDate', 'asc')
        );
        
        const payRunsSnapshot = await getDocs(payRunsQuery);
        const payRuns = [];
        
        let totalPaid = 0;
        payRunsSnapshot.forEach(doc => {
          const data = doc.data();
          totalPaid += data.totalAmount || 0;
          payRuns.push({
            id: doc.id,
            ...data,
            paymentDate: data.paymentDate?.toDate() || null
          });
        });
        
        // Fetch employees with payroll details
        const employeesQuery = query(
          collection(db, 'employeePayrollDetails'),
          where('businessId', '==', activeBusiness.id)
        );
        
        const employeesSnapshot = await getDocs(employeesQuery);
        const employees = [];
        
        let totalSalary = 0;
        employeesSnapshot.forEach(doc => {
          const data = doc.data();
          employees.push({
            id: doc.id,
            ...data
          });
          
          if (data.salaryType === 'monthly') {
            totalSalary += data.baseSalary || 0;
          } else if (data.salaryType === 'annual') {
            totalSalary += (data.baseSalary || 0) / 12;
          }
          // Hourly employees not included in avg calculation
        });
        
        // Fetch all payslips
        const payslipsQuery = query(
          collection(db, 'payslips'),
          where('businessId', '==', activeBusiness.id),
          where('paymentDate', '>=', startDate)
        );
        
        const payslipsSnapshot = await getDocs(payslipsQuery);
        const payslips = [];
        
        let totalTaxAmount = 0;
        let totalGrossAmount = 0;
        
        payslipsSnapshot.forEach(doc => {
          const data = doc.data();
          payslips.push({
            id: doc.id,
            ...data,
            paymentDate: data.paymentDate?.toDate() || null
          });
          
          // Calculate tax amounts
          const taxAmount = data.taxes?.reduce((sum, tax) => sum + (tax.amount || 0), 0) || 0;
          totalTaxAmount += taxAmount;
          totalGrossAmount += data.grossAmount || 0;
        });
        
        // Process monthly data
        const monthlyData = processMonthlyData(payRuns);
        
        // Process department distribution (mocked for now)
        // In a real app, you'd fetch employee departments
        const departmentData = [
          { department: 'Administration', amount: totalPaid * 0.2 },
          { department: 'Operations', amount: totalPaid * 0.3 },
          { department: 'Sales', amount: totalPaid * 0.25 },
          { department: 'Marketing', amount: totalPaid * 0.15 },
          { department: 'IT', amount: totalPaid * 0.1 }
        ];
        
        // Calculate tax distribution
        const taxRate = totalGrossAmount > 0 ? (totalTaxAmount / totalGrossAmount) * 100 : 0;
        
        const taxData = [
          { category: 'Income Tax', amount: totalTaxAmount * 0.7 },
          { category: 'Social Security', amount: totalTaxAmount * 0.15 },
          { category: 'Medicare', amount: totalTaxAmount * 0.1 },
          { category: 'Other Taxes', amount: totalTaxAmount * 0.05 }
        ];
        
        // Update state with all the data
        setPayrollData({
          summary: {
            totalPaid,
            employeeCount: employees.length,
            avgSalary: employees.length > 0 ? totalSalary / employees.length : 0,
            avgTaxRate: taxRate,
            payRunCount: payRuns.length
          },
          monthly: monthlyData,
          departmentDistribution: departmentData,
          taxDistribution: taxData
        });
        
      } catch (err) {
        console.error('Error fetching payroll report data:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPayrollData();
  }, [currentUser, activeBusiness, timeFrame]);
  
  // Process monthly data for the chart
  const processMonthlyData = (payRuns) => {
    const monthlyTotals = {};
    
    // Initialize all months with 0
    const today = new Date();
    
    if (timeFrame === 'year') {
      // Get all months of the current year
      for (let i = 0; i < 12; i++) {
        const monthName = new Date(today.getFullYear(), i, 1).toLocaleString('default', { month: 'short' });
        monthlyTotals[monthName] = 0;
      }
    } else if (timeFrame === 'quarter') {
      // Get 3 months of the current quarter
      const quarter = Math.floor(today.getMonth() / 3);
      for (let i = 0; i < 3; i++) {
        const monthName = new Date(today.getFullYear(), quarter * 3 + i, 1).toLocaleString('default', { month: 'short' });
        monthlyTotals[monthName] = 0;
      }
    } else if (timeFrame === 'month') {
      // Get all days of the current month (simplified to weeks)
      const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
      for (let week = 1; week <= 5; week++) {
        monthlyTotals[`Week ${week}`] = 0;
      }
    }
    
    // Sum the totals for each period
    payRuns.forEach(payRun => {
      if (!payRun.paymentDate) return;
      
      if (timeFrame === 'year' || timeFrame === 'quarter') {
        const monthName = payRun.paymentDate.toLocaleString('default', { month: 'short' });
        monthlyTotals[monthName] = (monthlyTotals[monthName] || 0) + (payRun.totalAmount || 0);
      } else if (timeFrame === 'month') {
        // Calculate week number (1-5)
        const day = payRun.paymentDate.getDate();
        const week = Math.ceil(day / 7);
        monthlyTotals[`Week ${week}`] = (monthlyTotals[`Week ${week}`] || 0) + (payRun.totalAmount || 0);
      }
    });
    
    // Convert to array format for chart.js
    return Object.entries(monthlyTotals).map(([label, amount]) => ({
      label,
      amount
    }));
  };
  
  // Prepare chart data
  const barChartData = {
    labels: payrollData.monthly.map(item => item.label),
    datasets: [
      {
        label: 'Payroll Amount',
        data: payrollData.monthly.map(item => item.amount),
        backgroundColor: '#190192',
        borderColor: '#190192',
        borderWidth: 1
      }
    ]
  };
  
  const departmentPieData = {
    labels: payrollData.departmentDistribution.map(item => item.department),
    datasets: [
      {
        data: payrollData.departmentDistribution.map(item => item.amount),
        backgroundColor: [
          '#190192',
          '#3B0DB5',
          '#5F25E8',
          '#8447FF',
          '#A875FF'
        ],
        borderWidth: 1
      }
    ]
  };
  
  const taxPieData = {
    labels: payrollData.taxDistribution.map(item => item.category),
    datasets: [
      {
        data: payrollData.taxDistribution.map(item => item.amount),
        backgroundColor: [
          '#190192',
          '#3B0DB5',
          '#5F25E8',
          '#8447FF'
        ],
        borderWidth: 1
      }
    ]
  };
  
  // Toggle expanded section
  const toggleSection = (section) => {
    if (expandedSection === section) {
      setExpandedSection('all');
    } else {
      setExpandedSection(section);
    }
  };
  
  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <button
          onClick={() => navigate('/payroll')}
          className="text-[#190192] hover:text-[#2C0DB5] flex items-center"
        >
          <ChevronLeft size={16} className="mr-1" />
          Back to Payroll
        </button>
        <h1 className="mt-2 text-xl font-bold text-gray-900">Payroll Reports</h1>
      </div>
      
      <div className="p-6">
        {/* Time Frame Selector */}
        <div className="mb-6 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <span className="text-gray-500">Time Frame:</span>
            <select
              value={timeFrame}
              onChange={(e) => setTimeFrame(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#190192] focus:border-[#190192]"
            >
              <option value="year">Year to Date</option>
              <option value="quarter">Current Quarter</option>
              <option value="month">Current Month</option>
            </select>
          </div>
          
          <button
            onClick={() => alert('Download report as PDF')}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 inline-flex items-center"
          >
            <Download size={16} className="mr-2" />
            Export Report
          </button>
        </div>
        
        {loading ? (
          <div className="flex justify-center items-center min-h-[300px]">
            <div className="animate-spin h-8 w-8 border-4 border-[#190192] rounded-full border-t-transparent"></div>
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
              <div className="bg-gradient-to-r from-[#190192] to-[#3B0DB5] text-white rounded-xl p-4 shadow-sm">
                <div className="flex items-center mb-2">
                  <DollarSign size={20} className="mr-2" />
                  <h3 className="text-sm font-medium">Total Payroll</h3>
                </div>
                <p className="text-2xl font-bold">{formatAmount(payrollData.summary.totalPaid)}</p>
                <p className="text-xs mt-1 text-purple-200">{timeFrame === 'year' ? 'Year to date' : timeFrame === 'quarter' ? 'Current quarter' : 'Current month'}</p>
              </div>
              
              <div className="bg-white rounded-xl p-4 border border-gray-200">
                <div className="flex items-center mb-2 text-[#190192]">
                  <User size={20} className="mr-2" />
                  <h3 className="text-sm font-medium">Employees</h3>
                </div>
                <p className="text-2xl font-bold text-gray-900">{payrollData.summary.employeeCount}</p>
                <p className="text-xs mt-1 text-gray-500">Active employees</p>
              </div>
              
              <div className="bg-white rounded-xl p-4 border border-gray-200">
                <div className="flex items-center mb-2 text-[#190192]">
                  <DollarSign size={20} className="mr-2" />
                  <h3 className="text-sm font-medium">Avg. Salary</h3>
                </div>
                <p className="text-2xl font-bold text-gray-900">{formatAmount(payrollData.summary.avgSalary)}</p>
                <p className="text-xs mt-1 text-gray-500">Per month</p>
              </div>
              
              <div className="bg-white rounded-xl p-4 border border-gray-200">
                <div className="flex items-center mb-2 text-[#190192]">
                  <PieChart size={20} className="mr-2" />
                  <h3 className="text-sm font-medium">Avg. Tax Rate</h3>
                </div>
                <p className="text-2xl font-bold text-gray-900">{payrollData.summary.avgTaxRate.toFixed(1)}%</p>
                <p className="text-xs mt-1 text-gray-500">Of gross earnings</p>
              </div>
              
              <div className="bg-white rounded-xl p-4 border border-gray-200">
                <div className="flex items-center mb-2 text-[#190192]">
                  <Calendar size={20} className="mr-2" />
                  <h3 className="text-sm font-medium">Pay Runs</h3>
                </div>
                <p className="text-2xl font-bold text-gray-900">{payrollData.summary.payRunCount}</p>
                <p className="text-xs mt-1 text-gray-500">Completed pay runs</p>
              </div>
            </div>
            
            {/* Expandable Report Sections */}
            <div className="space-y-6">
              {/* Monthly Payroll Distribution */}
              <div className={`bg-white rounded-xl border border-gray-200 overflow-hidden ${expandedSection === 'monthly' || expandedSection === 'all' ? 'shadow-sm' : ''}`}>
                <div 
                  className="px-6 py-4 border-b border-gray-200 flex justify-between items-center cursor-pointer"
                  onClick={() => toggleSection('monthly')}
                >
                  <h2 className="text-lg font-medium text-gray-900 flex items-center">
                    <BarChart2 size={20} className="mr-2 text-[#190192]" />
                    Payroll Distribution Over Time
                  </h2>
                  <ChevronDown 
                    size={20} 
                    className={`text-gray-400 transition-transform ${expandedSection === 'monthly' || expandedSection === 'all' ? 'transform rotate-180' : ''}`}
                  />
                </div>
                
                {(expandedSection === 'monthly' || expandedSection === 'all') && (
                  <div className="p-6">
                    <div className="h-80">
                      <Bar 
                        data={barChartData} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          scales: {
                            y: {
                              beginAtZero: true,
                              ticks: {
                                callback: function(value) {
                                  return formatAmount(value, { maximumSignificantDigits: 2 });
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
              
              {/* Department Distribution */}
              <div className={`bg-white rounded-xl border border-gray-200 overflow-hidden ${expandedSection === 'department' || expandedSection === 'all' ? 'shadow-sm' : ''}`}>
                <div 
                  className="px-6 py-4 border-b border-gray-200 flex justify-between items-center cursor-pointer"
                  onClick={() => toggleSection('department')}
                >
                  <h2 className="text-lg font-medium text-gray-900 flex items-center">
                    <Briefcase size={20} className="mr-2 text-[#190192]" />
                    Payroll by Department
                  </h2>
                  <ChevronDown 
                    size={20} 
                    className={`text-gray-400 transition-transform ${expandedSection === 'department' || expandedSection === 'all' ? 'transform rotate-180' : ''}`}
                  />
                </div>
                
                {(expandedSection === 'department' || expandedSection === 'all') && (
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="h-80 flex items-center justify-center">
                        <Pie 
                          data={departmentPieData} 
                          options={{
                            responsive: true,
                            maintainAspectRatio: false
                          }}
                        />
                      </div>
                      
                      <div className="space-y-4">
                        <h3 className="font-medium text-gray-900">Department Breakdown</h3>
                        <div className="space-y-2">
                          {payrollData.departmentDistribution.map((item, index) => (
                            <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                              <span className="font-medium">{item.department}</span>
                              <span>{formatAmount(item.amount)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Tax Distribution */}
              <div className={`bg-white rounded-xl border border-gray-200 overflow-hidden ${expandedSection === 'tax' || expandedSection === 'all' ? 'shadow-sm' : ''}`}>
                <div 
                  className="px-6 py-4 border-b border-gray-200 flex justify-between items-center cursor-pointer"
                  onClick={() => toggleSection('tax')}
                >
                  <h2 className="text-lg font-medium text-gray-900 flex items-center">
                    <FileText size={20} className="mr-2 text-[#190192]" />
                    Tax Distribution
                  </h2>
                  <ChevronDown 
                    size={20} 
                    className={`text-gray-400 transition-transform ${expandedSection === 'tax' || expandedSection === 'all' ? 'transform rotate-180' : ''}`}
                  />
                </div>
                
                {(expandedSection === 'tax' || expandedSection === 'all') && (
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="h-80 flex items-center justify-center">
                      <Pie 
                          data={taxPieData}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false
                          }}
                        />
                      </div>
                      
                      <div className="space-y-4">
                        <h3 className="font-medium text-gray-900">Tax Breakdown</h3>
                        <div className="space-y-2">
                          {payrollData.taxDistribution.map((item, index) => (
                            <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                              <span className="font-medium">{item.category}</span>
                              <span>{formatAmount(item.amount)}</span>
                            </div>
                          ))}
                        </div>
                        
                        <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                          <p className="text-sm text-gray-700">
                            <strong>Note:</strong> Tax distribution is calculated based on completed payrolls. 
                            Individual tax rates may vary based on employee circumstances.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Additional reports could be added here */}
            </div>
            
            {/* Report footer with disclaimer */}
            <div className="mt-8 p-4 bg-gray-50 rounded-lg text-sm text-gray-500">
              <p>
                This report is generated based on data from {timeFrame === 'year' ? 'the current year' : 
                timeFrame === 'quarter' ? 'the current quarter' : 'the current month'}. 
                For detailed historical reports or custom date ranges, please export the data.
              </p>
              <p className="mt-2">
                Last updated: {new Date().toLocaleString()}
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PayrollReports;